import React, { useState, useEffect, useCallback, useRef } from 'react';
import Chart from './Chart';
import { getBots, getBotStatus, getBotTrades, getBotLevels, getBotConfig, startBot, restartBot, stopBot, getBotStreamToken, getAlerts, getFleetOverview } from './api';

const REFRESH = 15000;

const s = {
  dark:   '#0a0a0a',
  card:   '#111111',
  border: '#222222',
  green:  '#00ff88',
  red:    '#ff4444',
  yellow: '#ffcc00',
  muted:  '#888888',
  white:  '#ffffff',
};

function badge(label, color) {
  return (
    <span style={{padding:'2px 8px',border:`1px solid ${color}`,borderRadius:'999px',color,fontSize:'0.7rem',background:color+'22'}}>
      {label}
    </span>
  );
}

function Card({ title, value, color }) {
  return (
    <div style={{background:s.card,border:`1px solid ${s.border}`,borderRadius:'8px',padding:'16px'}}>
      <p style={{color:s.muted,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'0.05em',margin:'0 0 6px'}}>{title}</p>
      <p style={{color:color||s.white,fontWeight:'bold',fontSize:'1.1rem',margin:0}}>{value}</p>
    </div>
  );
}

function PnL({ val }) {
  const c = val > 0 ? s.green : val < 0 ? s.red : s.muted;
  return <span style={{color:c,fontWeight:'bold'}}>{val > 0 ? '+' : ''}${val}</span>;
}

export default function Dashboard({ user, onLogout }) {
  const [bots, setBots]         = useState([]);
  const [sel, setSel]           = useState(null);
  const [status, setStatus]     = useState(null);
  const [trades, setTrades]     = useState([]);
  const [levels, setLevels]     = useState([]);
  const [config, setConfig]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [updated, setUpdated]   = useState('');
  const [tab, setTab]           = useState('overview');
  const [msg, setMsg]           = useState('');
  const [showAlerts, setShowAlerts] = useState(false);
  const [alerts, setAlerts]         = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [showFleet, setShowFleet] = useState(false);
  const [fleet, setFleet]         = useState([]);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const [logs, setLogs]         = useState([]);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!sel) return;
    if (wsRef.current) wsRef.current.close();
    setLogs([]); // clear stale lines from whichever bot was viewed previously
    let cancelled = false;
    // Connect to the CENTRAL relay, not the subscriber agent directly.
    // Subscriber servers firewall 443 to Server 1's IP only, so a direct
    // browser connection only works from a whitelisted location.
    const jwt = localStorage.getItem('token');
    if (!jwt) return;
    const wsUrl = window.location.origin.replace(/^http/, 'ws')
                  + `/api/bots/${sel.id}/stream?token=${encodeURIComponent(jwt)}`;
    const ws = new WebSocket(wsUrl);
    ws.onopen = () => {
      if (cancelled) { ws.close(); return; }
      wsRef.current = ws;
    };
    ws.onmessage = (e) => {
      if (cancelled || !e.data.trim()) return;
      setLogs(prev => [...prev.slice(-49), e.data]);
    };
    ws.onerror = () => {};
    return () => {
      cancelled = true;
      try { ws.close(); } catch (err) {}
      if (wsRef.current) wsRef.current.close();
    };
  }, [sel?.id]);

  const loadBots = useCallback(async () => {
    try {
      const data = await getBots();
      setBots(data);
      if (data.length > 0 && !sel) setSel(data[0]);
    } catch {}
  }, [sel]);

  const loadData = useCallback(async (id) => {
    try {
      const [st, tr, lv, cf] = await Promise.all([
        getBotStatus(id), getBotTrades(id), getBotLevels(id), getBotConfig(id)
      ]);
      setStatus(st); setTrades(tr); setLevels(lv); setConfig(cf);
      setUpdated(new Date().toLocaleTimeString());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadBots(); }, []);
  useEffect(() => {
    if (!showFleet) return;
    let cancelled = false;
    const fetchFleet = () => {
      setFleetLoading(true);
      getFleetOverview().then(data => { if (!cancelled) setFleet(data); })
        .catch(() => {})
        .finally(() => { if (!cancelled) setFleetLoading(false); });
    };
    fetchFleet();
    const iv = setInterval(fetchFleet, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [showFleet]);
  useEffect(() => {
    if (!showAlerts) return;
    let cancelled = false;
    const fetchAlerts = () => {
      setAlertsLoading(true);
      getAlerts().then(data => { if (!cancelled) setAlerts(data); })
        .catch(() => {})
        .finally(() => { if (!cancelled) setAlertsLoading(false); });
    };
    fetchAlerts();
    const iv = setInterval(fetchAlerts, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [showAlerts]);
  useEffect(() => {
    if (!sel) return;
    setLoading(true);
    loadData(sel.id);
    const iv = setInterval(() => loadData(sel.id), REFRESH);
    return () => clearInterval(iv);
  }, [sel]);

  const action = async (fn, label) => {
    setMsg(label + '...');
    try { await fn(sel.id); setMsg(label + ' ✓'); }
    catch { setMsg(label + ' failed'); }
    setTimeout(() => setMsg(''), 3000);
  };

  const totalPnl  = trades.reduce((s, t) => s + (t.pnl_usd || 0), 0);
  const wins      = trades.filter(t => t.pnl_pts > 0).length;
  const winRate   = trades.length ? Math.round(wins / trades.length * 100) : 0;

  const levelGroups = [
    { label: 'NY H / L',    keys: ['NYH','NYL'] },
    { label: 'NY Rolling (Today)', keys: ['RollNYH','RollNYL'] },
    { label: 'Asia',       keys: ['AsiaH','AsiaL'] },
    { label: 'London',     keys: ['LondonH','LondonL'] },
    { label: 'Globex',     keys: ['GlobexH','GlobexL'] },
    { label: 'Weekly',     keys: ['WeekH','WeekL'] },
    { label: 'Monthly',    keys: ['MonthH','MonthL'] },
  ];

  const levelColors = {
    NYH:'#f97316',NYL:'#f97316',
    RollNYH:'#22d3ee',RollNYL:'#22d3ee',
    AsiaH:'#a855f7',AsiaL:'#a855f7',
    LondonH:'#3b82f6',LondonL:'#3b82f6',
    GlobexH:'#14b8a6',GlobexL:'#14b8a6',
    WeekH:'#eab308',WeekL:'#eab308',
    MonthH:'#ef4444',MonthL:'#ef4444',
  };

  return (
    <div style={{minHeight:'100vh',background:s.dark,color:s.white,fontFamily:'Monaco,Courier New,monospace'}}>
      {/* Header */}
      <header style={{background:s.card,borderBottom:`1px solid ${s.border}`,padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          {isMobile && (
            <button onClick={() => setShowSidebar(!showSidebar)}
              style={{background:'none',border:`1px solid ${s.border}`,borderRadius:'4px',color:s.green,cursor:'pointer',padding:'4px 8px',fontSize:'1rem'}}>
              ☰
            </button>
          )}
          <h1 style={{color:s.green,margin:0,fontSize:isMobile?'1rem':'1.25rem',fontWeight:'bold'}}>⚡ KINETIC BOS</h1>
          {updated && !isMobile && <span style={{color:s.muted,fontSize:'0.7rem'}}>Updated {updated}</span>}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          {user?.is_admin && (
            <button onClick={() => setShowFleet(true)}
              style={{color:s.green,background:'none',border:`1px solid ${s.green}`,borderRadius:'4px',cursor:'pointer',fontSize:'0.75rem',padding:'4px 10px'}}>
              📊 Fleet
            </button>
          )}
          {user?.is_admin && (
            <button onClick={() => setShowAlerts(true)}
              style={{color:s.yellow,background:'none',border:`1px solid ${s.yellow}`,borderRadius:'4px',cursor:'pointer',fontSize:'0.75rem',padding:'4px 10px'}}>
              🔔 Alerts
            </button>
          )}
          <span style={{color:s.muted,fontSize:'0.8rem'}}>{user?.username}</span>
          <button onClick={onLogout} style={{color:s.muted,background:'none',border:'none',cursor:'pointer',fontSize:'0.75rem'}}>Sign Out</button>
        </div>
      </header>
      {showFleet && user?.is_admin && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.75)',zIndex:1000,display:'flex',alignItems:'flex-start',justifyContent:'center',paddingTop:'40px'}}>
          <div style={{background:s.card,border:`1px solid ${s.border}`,borderRadius:'8px',width:'min(900px, 94vw)',maxHeight:'80vh',display:'flex',flexDirection:'column'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 18px',borderBottom:`1px solid ${s.border}`}}>
              <h2 style={{color:s.white,fontSize:'1rem',margin:0}}>📊 Fleet Overview {fleetLoading && <span style={{color:s.muted,fontSize:'0.75rem'}}>(refreshing…)</span>}</h2>
              <button onClick={() => setShowFleet(false)}
                style={{background:'none',border:'none',color:s.muted,cursor:'pointer',fontSize:'1.1rem'}}>✕</button>
            </div>
            <div style={{overflowY:'auto',overflowX:'auto',padding:'8px 18px 18px'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem'}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${s.border}`,textAlign:'left'}}>
                    <th style={{padding:'8px 10px',color:s.muted,fontWeight:'normal'}}>Instance</th>
                    <th style={{padding:'8px 10px',color:s.muted,fontWeight:'normal'}}>Instrument</th>
                    <th style={{padding:'8px 10px',color:s.muted,fontWeight:'normal'}}>Status</th>
                    <th style={{padding:'8px 10px',color:s.muted,fontWeight:'normal'}}>State</th>
                    <th style={{padding:'8px 10px',color:s.muted,fontWeight:'normal'}}>In Trade</th>
                    <th style={{padding:'8px 10px',color:s.muted,fontWeight:'normal'}}>Trades</th>
                    <th style={{padding:'8px 10px',color:s.muted,fontWeight:'normal'}}>PnL</th>
                  </tr>
                </thead>
                <tbody>
                  {fleet.map(f => (
                    <tr key={f.id} style={{borderBottom:`1px solid ${s.border}55`}}>
                      <td style={{padding:'8px 10px',color:s.white}}>{f.name}</td>
                      <td style={{padding:'8px 10px',color:s.muted}}>{f.instrument}</td>
                      <td style={{padding:'8px 10px'}}>
                        {!f.reachable ? badge('UNREACHABLE', s.red)
                          : f.running ? badge('RUNNING', s.green)
                          : badge('STOPPED', s.yellow)}
                      </td>
                      <td style={{padding:'8px 10px',color:s.muted}}>{f.state || '—'}</td>
                      <td style={{padding:'8px 10px',color:f.in_trade ? s.green : s.muted}}>{f.in_trade ? 'Yes' : 'No'}</td>
                      <td style={{padding:'8px 10px',color:s.muted}}>{f.trades_taken != null ? `${f.trades_taken}/${f.max_trades}` : '—'}</td>
                      <td style={{padding:'8px 10px'}}>{f.pnl_usd != null ? <PnL val={f.pnl_usd} /> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {fleet.length === 0 && !fleetLoading && (
                <p style={{color:s.muted,fontSize:'0.85rem',padding:'20px 0'}}>No instances registered.</p>
              )}
            </div>
          </div>
        </div>
      )}
      {showAlerts && user?.is_admin && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.75)',zIndex:1000,display:'flex',alignItems:'flex-start',justifyContent:'center',paddingTop:'40px'}}>
          <div style={{background:s.card,border:`1px solid ${s.border}`,borderRadius:'8px',width:'min(700px, 92vw)',maxHeight:'80vh',display:'flex',flexDirection:'column'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 18px',borderBottom:`1px solid ${s.border}`}}>
              <h2 style={{color:s.white,fontSize:'1rem',margin:0}}>🔔 Fleet Alerts {alertsLoading && <span style={{color:s.muted,fontSize:'0.75rem'}}>(refreshing…)</span>}</h2>
              <button onClick={() => setShowAlerts(false)}
                style={{background:'none',border:'none',color:s.muted,cursor:'pointer',fontSize:'1.1rem'}}>✕</button>
            </div>
            <div style={{overflowY:'auto',padding:'8px 18px 18px'}}>
              {alerts.length === 0 && !alertsLoading && (
                <p style={{color:s.green,fontSize:'0.85rem',padding:'20px 0'}}>✓ No active alerts — every instance healthy.</p>
              )}
              {alerts.map(a => {
                const color = a.severity === 'critical' ? s.red : a.severity === 'warning' ? s.yellow : s.muted;
                return (
                  <div key={a.id} style={{borderLeft:`3px solid ${color}`,background:s.dark,borderRadius:'4px',padding:'10px 12px',marginTop:'10px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{color,fontWeight:'bold',fontSize:'0.75rem',textTransform:'uppercase'}}>{a.severity}</span>
                      <span style={{color:s.muted,fontSize:'0.7rem'}}>{a.created_at ? a.created_at.slice(0,19).replace('T',' ') : ''} UTC</span>
                    </div>
                    <p style={{color:s.white,fontSize:'0.85rem',margin:'6px 0 2px'}}>{a.message}</p>
                    <span style={{color:s.muted,fontSize:'0.7rem'}}>{a.bot_name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div style={{display:'flex',position:'relative'}}>
        {isMobile && showSidebar && (
          <div onClick={() => setShowSidebar(false)}
            style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.7)',zIndex:10}} />
        )}
        <aside style={{
          width:'200px',background:s.card,borderRight:`1px solid ${s.border}`,
          minHeight:'calc(100vh - 49px)',padding:'16px',
          ...(isMobile ? {
            position:'fixed',top:'49px',left:0,bottom:0,zIndex:20,
            transform:showSidebar?'translateX(0)':'translateX(-100%)',
            transition:'transform 0.2s ease'
          } : {})
        }}>
          <p style={{color:s.muted,fontSize:'0.65rem',textTransform:'uppercase',letterSpacing:'0.05em',marginTop:0}}>Bot Instances</p>
          {bots.map(b => (
            <button key={b.id} onClick={() => { setSel(b); setShowSidebar(false); }}
              style={{width:'100%',textAlign:'left',padding:'10px',borderRadius:'6px',marginBottom:'6px',cursor:'pointer',
                background: sel?.id===b.id ? s.green+'11' : 'transparent',
                border: `1px solid ${sel?.id===b.id ? s.green+'44' : 'transparent'}`,
                color: sel?.id===b.id ? s.green : s.muted}}>
              <div style={{fontWeight:'bold',fontSize:'0.875rem'}}>{b.instrument}</div>
              <div style={{fontSize:'0.7rem',opacity:0.7}}>{b.name}</div>
            </button>
          ))}
        </aside>

        {/* Main */}
        <main style={{flex:1,padding:isMobile?'12px':'24px',minWidth:0}}>
          {bots.length === 0 ? (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'200px',flexDirection:'column',gap:'12px'}}>
              <span style={{color:s.muted,fontSize:'1.5rem'}}>⚡</span>
              <span style={{color:s.muted}}>No bot instances assigned to your account.</span>
              <span style={{color:s.muted,fontSize:'0.75rem'}}>Contact your administrator.</span>
            </div>
          ) : loading ? (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'200px'}}>
              <span style={{color:s.green}}>Loading...</span>
            </div>
          ) : (
            <>
              {/* Top bar */}
              <div style={{display:'flex',alignItems:isMobile?'flex-start':'center',flexDirection:isMobile?'column':'row',justifyContent:'space-between',marginBottom:'16px',gap:isMobile?'8px':'0'}}>
                <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                  <h2 style={{margin:0,fontSize:'1.1rem'}}>{sel?.name}</h2>
                  {status && badge(status.running ? status.state : 'OFFLINE', status.running ? (status.state==='ARMED'?s.green:s.yellow) : s.red)}
                  {status?.in_trade ? (
                    <div style={{display:'flex',alignItems:'center',gap:'8px',background:'#00ff8811',border:'1px solid #00ff8844',borderRadius:'6px',padding:'6px 12px',flexWrap:'wrap'}}>
                      <span style={{color:status.trade_direction==='SHORT'?s.red:s.green,fontWeight:'bold',fontSize:'0.75rem'}}>
                        ● {status.trade_direction||'IN'} TRADE
                      </span>
                      <span style={{color:s.muted,fontSize:'0.7rem'}}>|</span>
                      <span style={{color:s.muted,fontSize:'0.7rem'}}>Entry</span>
                      <span style={{color:s.white,fontSize:'0.75rem',fontWeight:'bold'}}>{status.entry_price||status.last_price}</span>
                      <span style={{color:s.muted,fontSize:'0.7rem'}}>|</span>
                      <span style={{color:s.muted,fontSize:'0.7rem'}}>Target</span>
                      <span style={{color:s.green,fontSize:'0.75rem',fontWeight:'bold'}}>{status.target_price||'—'}</span>
                      <span style={{color:s.muted,fontSize:'0.7rem'}}>|</span>
                      <span style={{color:s.muted,fontSize:'0.7rem'}}>Stop</span>
                      <span style={{color:s.red,fontSize:'0.75rem',fontWeight:'bold'}}>{status.stop_price||'—'}</span>
                      <span style={{color:s.muted,fontSize:'0.7rem'}}>|</span>
                      <span style={{color:s.muted,fontSize:'0.7rem'}}>P&L</span>
                      <span style={{color:status.unrealised_pnl_usd>0?s.green:status.unrealised_pnl_usd<0?s.red:s.muted,fontSize:'0.75rem',fontWeight:'bold'}}>
                        {status.unrealised_pnl_usd>0?'+':''}{status.unrealised_pnl_usd||0} USD
                      </span>
                    </div>
                  ) : (
                    <div style={{display:'flex',alignItems:'center',gap:'8px',background:'#88888811',border:'1px solid #88888833',borderRadius:'6px',padding:'6px 12px'}}>
                      <span style={{color:s.muted,fontSize:'0.75rem'}}>● FLAT</span>
                      <span style={{color:s.muted,fontSize:'0.7rem'}}>|</span>
                      <span style={{color:s.muted,fontSize:'0.7rem'}}>Waiting for setup</span>
                    </div>
                  )}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                  {msg && <span style={{color:s.green,fontSize:'0.75rem'}}>{msg}</span>}
                  <button onClick={() => action(startBot,'Start')}
                    style={{padding:'4px 12px',background:'#3b82f611',border:'1px solid #3b82f644',color:'#3b82f6',borderRadius:'4px',cursor:'pointer',fontSize:'0.75rem'}}>
                    Start
                  </button>
                  <button onClick={() => action(restartBot,'Restart')}
                    style={{padding:'4px 12px',background:s.green+'11',border:`1px solid ${s.green}44`,color:s.green,borderRadius:'4px',cursor:'pointer',fontSize:'0.75rem'}}>
                    Restart
                  </button>
                  <button onClick={() => action(stopBot,'Stop')}
                    style={{padding:'4px 12px',background:s.red+'11',border:`1px solid ${s.red}44`,color:s.red,borderRadius:'4px',cursor:'pointer',fontSize:'0.75rem'}}>
                    Stop
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div style={{display:'flex',gap:'4px',marginBottom:'24px',borderBottom:`1px solid ${s.border}`}}>
                {['overview','chart','trades','levels','config'].map(t => (
                  <button key={t} onClick={() => setTab(t)}
                    style={{padding:'8px 16px',background:'none',border:'none',cursor:'pointer',fontSize:'0.875rem',textTransform:'capitalize',
                      color: tab===t ? s.green : s.muted,
                      borderBottom: tab===t ? `2px solid ${s.green}` : '2px solid transparent'}}>
                    {t}
                  </button>
                ))}
              </div>

              {/* Overview */}
              {tab==='overview' && (
                <div>
                  <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(4,1fr)',gap:isMobile?'8px':'16px',marginBottom:'16px'}}>
                    <Card title="State"        value={status?.state||'—'} color={s.green} />
                    <Card title="Session P&L"  value={<PnL val={status?.pnl_usd||0} />} />
                    <Card title="Trades"       value={`${status?.trades_taken||0}/${status?.max_trades||3}`} />
                    <Card title="Win Rate"     value={`${winRate}%`} color={winRate>=50?s.green:s.red} />
                    <Card title="Total P&L"    value={<PnL val={totalPnl} />} />
                    <Card title="Total Trades" value={trades.length} />
                    <Card title="Instrument"   value={status?.instrument||'—'} color={s.green} />
                    <Card title="PID"          value={status?.pid||'—'} color={s.muted} />
                  </div>
                  <div style={{background:s.card,border:`1px solid ${s.border}`,borderRadius:'8px',padding:'16px'}}>
                    <p style={{color:s.muted,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'0.05em',margin:'0 0 12px'}}>
                      Live Feed <span style={{color:s.green,marginLeft:'8px'}}>● LIVE</span>
                    </p>
                    <div style={{height:isMobile?'160px':'220px',overflowY:'auto',fontFamily:'monospace',fontSize:'0.7rem'}}>
                      {logs.length===0 ? (
                        <span style={{color:s.muted}}>Waiting for events...</span>
                      ) : (
                        [...logs].reverse().map((line,i) => {
                          const c = line.includes('ENTRY AUTHORIZED')?s.green
                            :line.includes('position CLOSED')||line.includes('trade logged')?s.green
                            :line.includes('HARD EXIT')||line.includes('ERROR')||line.includes('BLOCKED')?s.red
                            :line.includes('WARNING')||line.includes('timed out')?s.yellow
                            :line.includes('Displacement CONFIRMED')||line.includes('Retracement VALID')?'#3b82f6'
                            :line.includes('BOS detected')||line.includes('BOS CONFIRMED')?'#a855f7'
                            :line.includes('Session direction SET')?s.green
                            :line.includes('Order placed')||line.includes('Close order')?'#f97316'
                            :line.includes('STATUS')?'#334155'
                            :s.muted;
                          return <div key={i} style={{color:c,padding:'2px 0',borderBottom:`1px solid ${s.border}22`,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{line}</div>;
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Trades */}
              {tab==='trades' && (
                <div style={{background:s.card,border:`1px solid ${s.border}`,borderRadius:'8px',overflow:'hidden',overflowX:'auto'}}>
                  {trades.length===0 ? (
                    <div style={{padding:'48px',textAlign:'center',color:s.muted}}>No trades yet</div>
                  ) : (
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead>
                        <tr style={{background:s.dark,borderBottom:`1px solid ${s.border}`}}>
                          {['Time','Dir','Entry','Exit','P&L pts','P&L $','Reason'].map(h => (
                            <th key={h} style={{padding:'8px 12px',textAlign:'left',color:s.muted,fontSize:'0.7rem',textTransform:'uppercase'}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...trades].reverse().map((t,i) => {
                          const c = t.pnl_pts>0?s.green:s.red;
                          const sg = t.pnl_pts>0?'+':'';
                          return (
                            <tr key={i} style={{borderBottom:`1px solid ${s.border}22`}}>
                              <td style={{padding:'8px 12px',fontSize:'0.75rem',color:s.muted}}>{t.timestamp ? new Date(t.timestamp.replace(' ', 'T') + 'Z').toLocaleString('en-US', {
                                timeZone: 'America/New_York', month: '2-digit', day: '2-digit',
                                hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
                              }) : '—'}</td>
                              <td style={{padding:'8px 12px',fontSize:'0.75rem',fontWeight:'bold',color:t.direction==='LONG'?s.green:s.red}}>
                                {t.direction==='LONG'?'▲':'▼'} {t.direction}
                              </td>
                              <td style={{padding:'8px 12px',fontSize:'0.75rem'}}>{t.entry}</td>
                              <td style={{padding:'8px 12px',fontSize:'0.75rem'}}>{t.exit||'—'}</td>
                              <td style={{padding:'8px 12px',fontSize:'0.75rem',fontWeight:'bold',color:c}}>{sg}{t.pnl_pts}pt</td>
                              <td style={{padding:'8px 12px',fontSize:'0.75rem',fontWeight:'bold',color:c}}>{sg}${t.pnl_usd}</td>
                              <td style={{padding:'8px 12px',fontSize:'0.75rem',color:s.muted}}>{t.reason?.replace(/_/g,' ')}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Levels */}
              {tab==='levels' && (
                <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(1,1fr)':'repeat(3,1fr)',gap:'16px'}}>
                  {levelGroups.map(g => (
                    <div key={g.label} style={{background:s.card,border:`1px solid ${s.border}`,borderRadius:'8px',padding:'16px'}}>
                      <p style={{color:s.muted,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'0.05em',margin:'0 0 12px'}}>{g.label}</p>
                      {g.keys.map(k => {
                        const lv = levels.find(l => l.type===k);
                        return (
                          <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${s.border}33`}}>
                            <span style={{color:levelColors[k]||s.muted,fontSize:'0.75rem'}}>{k}</span>
                            <span style={{color:s.white,fontSize:'0.75rem',fontFamily:'monospace'}}>{lv ? lv.price.toFixed(2) : '—'}</span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {/* Config */}
              {tab==='chart' && (
                <Chart botId={sel?.id} wsLogs={logs} />
              )}
              {tab==='config' && config && (
                <div style={{background:s.card,border:`1px solid ${s.border}`,borderRadius:'8px',padding:'24px'}}>
                  <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(1,1fr)':'repeat(2,1fr)',gap:'8px'}}>
                    {Object.entries(config).map(([k,v]) => (
                      <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:`1px solid ${s.border}33`}}>
                        <span style={{color:s.muted,fontSize:'0.875rem',textTransform:'capitalize'}}>{k.replace(/_/g,' ')}</span>
                        <span style={{color:s.green,fontFamily:'monospace',fontWeight:'bold'}}>{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
